import { FlagReason, FlagSubreason } from '@modules/HappinessModule/enums/FlagsReasons';

export interface FlagReasonOptionsData {
  label: string;
  description: string;
  options: {
    value: FlagReason;
    description: string;
    subReasons: { value: FlagSubreason; description: string }[] | null;
  }[];
}
export interface FlagReasonsOptionsData {
  voluntary: FlagReasonOptionsData;
  involuntary: FlagReasonOptionsData;
}

export const flagReasonsOptions: FlagReasonsOptionsData = {
  voluntary: {
    label: 'Teammate initiated',
    description:
      'Attrition risk case generated when an AgileEngineer willingly raises a concern that jeopardizes the continuity of their collaboration with AE.\n\nIt can be caused both by dissatisfaction with internal practices/conditions while working with AE or its clients, or external circumstances that are more attractive or prevent them from continuing to collaborate with AE.',
    options: [
      {
        value: FlagReason.JobOfferPackage,
        description:
          'Motives related to a job offer specifics (current AE offer/new offer), which includes compensation, benefits and additional perks that a company provides their collaborators with.',
        subReasons: [
          {
            value: FlagSubreason.Compensation,
            description: 'Base and variable monetary compensation.',
          },
          {
            value: FlagSubreason.ContractConditions,
            description:
              "Perks, benefits, policies, legal/tax compliance (e.g.: LLC)\n\n*LLC: those cases where teammate voluntary decides to leave AE because they don't agree with change of tax policy\n*Relocation: those cases where teammate decides to accept eternal offer to relocate, mainly for higher compensation",
          },
        ],
      },
      {
        value: FlagReason.ProjectDepartmentV,
        description:
          'Motives related to the professional environment and development opportunities a company offers to its collaborators.',
        subReasons: [
          {
            value: FlagSubreason.TechnologyStack,
            description:
              'Combination of technologies, programming languages, frameworks, databases, tools and/or applications the company/project uses.',
          },
          {
            value: FlagSubreason.ContentTasks,
            description: 'Responsibilities under the scope of an specific role/person.',
          },
          {
            value: FlagSubreason.LeadTeam,
            description:
              'Group of individuals the collaborator works directly/has continuous interaction with. This includes both teammates/coworkers and leaders/superiors. ',
          },
          {
            value: FlagSubreason.ProfessionalDevelopment,
            description:
              'Opportunities/support provided to the collaborator by AE/client to promote their professional growth. This includes coaching, mentoring, skills development, networking and career pathing.',
          },
          {
            value: FlagSubreason.ClientDepartment,
            description: "AE's/client's environment, culture, practices, processes, values and internal dynamics.",
          },
        ],
      },
      {
        value: FlagReason.Personal,
        description:
          "Motives related to a collaborator's individual experiences/circumstances that impact their possibility/willingness to work for the company.",
        subReasons: [
          {
            value: FlagSubreason.OwnBusinessStudies,
            description: "Collaborator's individual professional or educational projects.",
          },
          {
            value: FlagSubreason.Health,
            description:
              "Physical, mental, or emotional condition that adversely affect the collaborator's safety, wellness or possibility of providing working services as usual. This may include both personal or relatives' cases.",
          },
          {
            value: FlagSubreason.MilitaryService,
            description: 'The person is going to serve in the army.',
          },
        ],
      },
      {
        value: FlagReason.Relocation,
        description: 'Desire/Opportunity of travelling or relocating to another country',
        subReasons: [
          {
            value: FlagSubreason.PersonalOrProfessional,
            description:
              'Desire/Opportunity of travelling or relocating to another country for personal reasons, or associated with a job opportunity.',
          },
          {
            value: FlagSubreason.VisaSponsorship,
            description:
              'Desire/Opportunity of being sponsored by AgileEngine for a work visa in any particular country requiring it.',
          },
        ],
      },
    ],
  },
  involuntary: {
    label: 'Manager or client initiated',
    description:
      "Attrition risk case generated when a concern is raised by AgileEngine or one of our clients that jeopardizes the continuity of an AgileEngineer's collaboration with AE.",
    options: [
      {
        value: FlagReason.ProjectDepartmentI,
        description:
          "Motives related to circumstances beyond the collaborator's scope of action, that imply the inability of the company to continue the working relationship.",
        subReasons: [
          {
            value: FlagSubreason.BudgetIssues,
            description: 'To be used only when the assignment ends due to budget constraints.',
          },
          {
            value: FlagSubreason.EndProject,
            description: `To be used when the assignment is either completed as agreed with no renewal or interrupted due to a shift in business priorities or project completion.\n
            The cut-off should not be linked to any other more relevant reasons, whether voluntary or involuntary.`,
          },
          {
            value: FlagSubreason.DirectHireConversion,
            description:
              'The client has either expressed interest or there are signals suggesting they may be planning to hire the AgileEngineer directly.',
          },
        ],
      },
      {
        value: FlagReason.Performance,
        description:
          "The collaborator's performance is not aligned with the expectations of the client/lead/team for their role/seniority. The mismatch might be related to the quality, quantity or speed of the work the person is doing.",
        subReasons: [
          {
            value: FlagSubreason.HardSkills,
            description: "Performance issues in skills related to the role's technical responsibilities.",
          },
          {
            value: FlagSubreason.SoftSkills,
            description:
              'Performance issues in skills related to attitude, communication, commitment, people management, etc.',
          },
          {
            value: FlagSubreason.HardAndSoftSkills,
            description:
              "Performance issues in skills related to both the role's techinical responsibilities and the attitude, communication, commitment, people management, etc.",
          },
        ],
      },
      {
        value: FlagReason.CulturalMismatch,
        description:
          "Misalignment between AE/client and the AgileEngineer in relation to either AE's or the client's values, expectations, or work practices, leading to performance, compliance, or engagement issues.",
        subReasons: [
          {
            value: FlagSubreason.JobAbandonment,
            description:
              "The collaborator doesn't report to work as scheduled, has no intention of returning to the job and doesn't notify AE/the client of their intention to quit.",
          },
          {
            value: FlagSubreason.LegalCompliance,
            description:
              'Not compliance with legal/contractual approach, internal policies, etc.\n\n*LLC: those cases where teammate wants to stay in AE but we need to move forward with termination due to them not being disposed to become tax compliant',
          },
          {
            value: FlagSubreason.ClientMismatch,
            description: `When a client requests to end cooperation with an AgileEngineer, citing performance issues, but after a deeper analysis, it's clear that the real reason is any of the following: \n
              1. The working dynamic wasn't a good match between the contractor and the client (including time zone challenges).\n
              2. The job description didn't fully align with what the client expected.\n
              3. The technology stack changed mid-assignment, and since it's not the teammate's strength, their performance dropped.`,
          },
        ],
      },
      {
        value: FlagReason.PassedAway,
        description: "Collaborator's death",
        subReasons: null,
      },
    ],
  },
};
